import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { getPremiumStatus } from "@/lib/premiumApi";
import BusinessBuilder from "@/components/site/BusinessBuilder";
import { toast } from "sonner";

const FACTOR_LABELS = {
  demand: "Demand",
  competition: "Open field",
  monetization: "Monetization",
  timing: "Timing",
};

const scoreColor = (s) => (s >= 70 ? "#0a7d33" : s >= 45 ? "#b45309" : "#ff3b30");

export const OpportunityPanel = ({ trendId, term }) => {
  const [score, setScore] = useState(null);
  const [loadingScore, setLoadingScore] = useState(true);
  const [plan, setPlan] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [needsPremium, setNeedsPremium] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    setScore(null);
    setPlan(null);
    setNeedsPremium(false);
    setLoadingScore(true);
    api.get(`/opportunity/score/${trendId}`)
      .then((r) => setScore(r.data))
      .catch(() => {})
      .finally(() => setLoadingScore(false));
    getPremiumStatus().then((s) => setUnlocked(!!s.premium));
    // Restore a previously generated plan (owner/premium only — 402/404 are silent)
    api.get(`/opportunity/business/latest/${trendId}`)
      .then((r) => setPlan(r.data.plan))
      .catch(() => {});
  }, [trendId]);

  const generate = async () => {
    setGenerating(true);
    setNeedsPremium(false);
    try {
      const { data } = await api.post("/opportunity/business", { trend_id: trendId });
      setPlan(data.plan);
      toast.success("Business plan generated");
    } catch (e) {
      if (e?.response?.status === 402) {
        setNeedsPremium(true);
      } else {
        toast.error(e?.response?.data?.detail || "Generation failed — try again");
      }
    }
    setGenerating(false);
  };

  return (
    <section data-testid="opportunity-panel" className="border-b border-black">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">
        <div className="overline text-[#52525b] mb-2">Opportunity Engine · AI analysis</div>
        <h2 className="font-display font-extrabold text-3xl lg:text-5xl tracking-tighter mb-8">
          Is there a business<br />hiding inside <span className="text-[#ff3b30]">{term}</span>?
        </h2>

        {loadingScore && (
          <div data-testid="opportunity-loading" className="border border-black p-8 font-mono text-xs text-[#52525b]">
            Scoring the opportunity with AI…
          </div>
        )}

        {score && (
          <div className="grid grid-cols-1 lg:grid-cols-12 border border-black">
            <div className="lg:col-span-3 p-6 border-b lg:border-b-0 lg:border-r border-black flex flex-col justify-center items-start">
              <div className="overline text-[#52525b]">Opportunity score</div>
              <div
                data-testid="opportunity-score"
                className="font-display font-extrabold tracking-tighter text-7xl lg:text-8xl"
                style={{ color: scoreColor(score.score) }}
              >
                {score.score}
              </div>
              <div className="font-mono text-xs text-[#52525b]">/ 100</div>
            </div>
            <div className="lg:col-span-9 p-6">
              <p className="font-display font-bold text-xl lg:text-2xl tracking-tight mb-6">
                &ldquo;{score.verdict}&rdquo;
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-black border border-black">
                {Object.entries(score.factors || {}).map(([k, f]) => (
                  <div key={k} className="bg-white p-4" data-testid={`opportunity-factor-${k}`}>
                    <div className="overline text-[#52525b]">{FACTOR_LABELS[k] || k}</div>
                    <div className="font-display font-extrabold text-3xl mt-1" style={{ color: scoreColor(f.score) }}>
                      {f.score}
                    </div>
                    <div className="font-mono text-[11px] text-[#52525b] mt-1 leading-tight">{f.note}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Business generator */}
        <div className="mt-8">
          {!plan && !needsPremium && (
            <button
              data-testid="generate-business-btn"
              onClick={generate}
              disabled={generating}
              className="hard-shadow border border-black bg-black text-white px-8 py-4 overline disabled:opacity-50"
            >
              {generating ? "Building your business…" : `Generate a business around "${term}" →`}
            </button>
          )}
          {!plan && !needsPremium && !unlocked && (
            <div className="mt-2 font-mono text-xs text-[#52525b]">
              Premium feature · <Link to="/premium" className="underline">$4.99/mo</Link> · owners &amp; members only
            </div>
          )}

          {needsPremium && (
            <div data-testid="opportunity-paywall" className="border border-black bg-[#f4f4f5] p-8">
              <div className="overline text-[#ff3b30] mb-2">Premium feature</div>
              <h3 className="font-display font-extrabold text-2xl lg:text-3xl tracking-tight">
                Unlock the AI Business Builder.
              </h3>
              <p className="mt-3 text-[#52525b] max-w-xl">
                Full business plans — name, products, pricing, marketing channels, first steps and
                revenue forecast — for every trend on Universtrend. Plus an ad-free experience.
              </p>
              <Link
                to="/premium"
                data-testid="paywall-premium-link"
                className="hard-shadow inline-block mt-6 border border-black bg-[#ff3b30] text-white px-8 py-4 overline"
              >
                Go Premium · $4.99/mo →
              </Link>
            </div>
          )}

          {plan && (
            <div data-testid="business-plan" className="border border-black">
              <div className="p-6 lg:p-8 border-b border-black bg-[#0a0a0a] text-white">
                <div className="overline text-[#a1a1aa]">Your AI business plan</div>
                <h3 className="font-display font-extrabold text-3xl lg:text-5xl tracking-tighter mt-1">
                  {plan.business_name}
                </h3>
                <div className="mt-2 font-mono text-sm text-[#a1a1aa]">
                  {plan.domain_idea} · &ldquo;{plan.tagline}&rdquo;
                </div>
                <p className="mt-4 max-w-2xl text-[#d4d4d8]">{plan.elevator_pitch}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-black">
                {(plan.products || []).map((p) => (
                  <div key={p.name} className="p-6">
                    <div className="overline text-[#52525b]">{p.type}</div>
                    <div className="font-display font-bold text-xl tracking-tight mt-1">{p.name}</div>
                    <p className="text-sm text-[#52525b] mt-2">{p.description}</p>
                    <div className="font-mono text-sm mt-3 text-[#ff3b30]">{p.price}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 border-t border-black divide-y lg:divide-y-0 lg:divide-x divide-black">
                <div className="p-6">
                  <div className="overline text-[#52525b] mb-3">First 5 steps</div>
                  <ol className="space-y-2 text-sm">
                    {(plan.first_steps || []).map((s, i) => (
                      <li key={s}><span className="font-mono text-[#52525b] mr-2">{String(i + 1).padStart(2, "0")}</span>{s}</li>
                    ))}
                  </ol>
                </div>
                <div className="p-6">
                  <div className="overline text-[#52525b] mb-3">Marketing &amp; numbers</div>
                  <ul className="space-y-2 text-sm">
                    {(plan.marketing_channels || []).map((m) => (
                      <li key={m}>· {m}</li>
                    ))}
                  </ul>
                  <div className="mt-4 grid grid-cols-3 gap-px bg-black border border-black font-mono text-xs">
                    <div className="bg-white p-3"><div className="text-[#52525b]">Startup cost</div><div className="font-bold mt-1">{plan.est_startup_cost}</div></div>
                    <div className="bg-white p-3"><div className="text-[#52525b]">Revenue potential</div><div className="font-bold mt-1">{plan.monthly_revenue_potential}</div></div>
                    <div className="bg-white p-3"><div className="text-[#52525b]">Difficulty</div><div className="font-bold mt-1 uppercase">{plan.difficulty}</div></div>
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-black flex flex-wrap gap-3 items-center justify-between">
                <div className="font-mono text-xs text-[#52525b]">Revenue model: {plan.revenue_model}</div>
                <button
                  data-testid="regenerate-business-btn"
                  onClick={generate}
                  disabled={generating}
                  className="border border-black px-5 py-2 overline hover:bg-black hover:text-white disabled:opacity-50"
                >
                  {generating ? "…" : "Regenerate ↻"}
                </button>
              </div>
            </div>
          )}

          {plan && (
            <BusinessBuilder
              trendId={trendId}
              businessName={plan.business_name}
              tagline={plan.tagline}
              pitch={plan.elevator_pitch}
              products={plan.products}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default OpportunityPanel;
